/**
 * Phase 21.3a5 Batch 2 — 🏠 Homepage Admin Controls + Boost Pulse Audit.
 *
 * Single admin section that mounts inside AdminControlCenterPage.
 * Sub-cards:
 *   1. Weekly Submission Timer  (enable/disable + deadline day/hour/minute)
 *   2. RGM Studio Live Promo Card (enable + title/text/CTA/image)
 *   3. Announcement Presentation (default label size / title size / display mode)
 *   4. Boost Pulse controls     (global + per-user disable list + read-only audit)
 *
 * All writes go through:
 *   PUT  /api/admin/homepage/weekly-timer
 *   PUT  /api/admin/homepage/live-promo-card
 *   PUT  /api/admin/homepage/announcement-presentation
 *   PUT  /api/admin/homepage/boost-pulse
 *   POST /api/admin/homepage/boost-pulse/toggle-user
 *   GET  /api/admin/boost-pulse/audit
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SIZES = [
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" },
];
const MODES = [
  { value: "full",         label: "Full (label + title + banner)" },
  { value: "banner_only",  label: "Banner only" },
  { value: "title_only",   label: "Title only" },
  { value: "label_only",   label: "Label only" },
];

const Card = ({ title, children, testId }) => (
  <div
    className="rounded-xl border border-white/10 bg-white/[0.03] p-4 sm:p-5"
    data-testid={testId}
  >
    <h3 className="text-white font-black text-sm mb-3">{title}</h3>
    {children}
  </div>
);

const NumberInput = ({ value, onChange, min, max, testId }) => (
  <Input
    type="number"
    min={min}
    max={max}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
    className="bg-white/5 border-white/10 text-white h-9 w-24"
    data-testid={testId}
  />
);

const HomepageControlsSection = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // section key currently saving

  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const [disableUid, setDisableUid] = useState("");

  const loadCfg = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/homepage/config");
      setCfg(r.data);
    } catch (e) {
      toast.error("Failed to load homepage config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCfg();
  }, [loadCfg]);

  const patchSection = async (key, path, body, successMsg) => {
    setSaving(key);
    try {
      const r = await api.put(path, body);
      setCfg((prev) => ({ ...(prev || {}), ...r.data }));
      toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const r = await api.get("/admin/boost-pulse/audit");
      setAudit(r.data);
    } catch (e) {
      toast.error("Failed to load Boost Pulse audit");
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const toggleUser = async (uid, disabled) => {
    try {
      await api.post("/admin/homepage/boost-pulse/toggle-user", {
        user_id: uid,
        disabled,
      });
      toast.success(disabled ? "Widget disabled for user" : "Widget re-enabled");
      // Refresh both — the audit shows `widget_disabled`, config drives it.
      await loadCfg();
      if (audit) await loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  if (loading || !cfg) {
    return (
      <p className="text-sm text-gray-500" data-testid="hp-controls-loading">
        Loading homepage controls…
      </p>
    );
  }

  const wt = cfg.weekly_timer || {};
  const lp = cfg.live_promo_card || {};
  const ap = cfg.announcement_presentation || {};
  const bp = cfg.boost_pulse || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

      {/* 1 · Weekly Submission Timer */}
      <Card title="🗓 Weekly Submission Timer" testId="hp-weekly-timer-card">
        <div className="flex items-center gap-3 mb-3">
          <input
            id="wt-enabled"
            type="checkbox"
            checked={!!wt.enabled}
            onChange={(e) =>
              setCfg({ ...cfg, weekly_timer: { ...wt, enabled: e.target.checked } })
            }
            className="w-4 h-4"
            data-testid="hp-weekly-timer-enabled"
          />
          <label htmlFor="wt-enabled" className="text-sm text-white">
            Show submission timer on homepage
          </label>
        </div>
        <div className="grid grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-[11px] text-gray-400 uppercase tracking-wider">Deadline day (UTC)</Label>
            <select
              value={wt.deadline_day_of_week ?? 4}
              onChange={(e) =>
                setCfg({ ...cfg, weekly_timer: { ...wt, deadline_day_of_week: Number(e.target.value) } })
              }
              className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
              data-testid="hp-weekly-timer-day"
            >
              {DAY_LABELS.map((d, i) => (
                <option key={d} value={i} className="bg-[#13131a]">{d}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[11px] text-gray-400 uppercase tracking-wider">Hour</Label>
            <NumberInput
              value={wt.deadline_hour_utc}
              onChange={(v) => setCfg({ ...cfg, weekly_timer: { ...wt, deadline_hour_utc: v } })}
              min={0} max={23}
              testId="hp-weekly-timer-hour"
            />
          </div>
          <div>
            <Label className="text-[11px] text-gray-400 uppercase tracking-wider">Minute</Label>
            <NumberInput
              value={wt.deadline_minute_utc}
              onChange={(v) => setCfg({ ...cfg, weekly_timer: { ...wt, deadline_minute_utc: v } })}
              min={0} max={59}
              testId="hp-weekly-timer-minute"
            />
          </div>
        </div>
        <div className="mt-4">
          <Button
            className="bg-purple-600 hover:bg-purple-500 text-white"
            disabled={saving === "wt"}
            onClick={() =>
              patchSection(
                "wt",
                "/admin/homepage/weekly-timer",
                {
                  enabled: !!wt.enabled,
                  deadline_day_of_week: Number(wt.deadline_day_of_week ?? 4),
                  deadline_hour_utc: Number(wt.deadline_hour_utc ?? 23),
                  deadline_minute_utc: Number(wt.deadline_minute_utc ?? 59),
                },
                "Weekly timer saved",
              )
            }
            data-testid="hp-weekly-timer-save"
          >
            {saving === "wt" ? "Saving…" : "Save weekly timer"}
          </Button>
        </div>
      </Card>

      {/* 2 · RGM Studio Live Promo Card */}
      <Card title="📡 RGM Studio Live Promo Card" testId="hp-live-promo-card">
        <div className="flex items-center gap-3 mb-3">
          <input
            id="lp-enabled"
            type="checkbox"
            checked={!!lp.enabled}
            onChange={(e) =>
              setCfg({ ...cfg, live_promo_card: { ...lp, enabled: e.target.checked } })
            }
            className="w-4 h-4"
            data-testid="hp-live-promo-enabled"
          />
          <label htmlFor="lp-enabled" className="text-sm text-white">
            Show Live Promo Card on homepage
          </label>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] text-gray-400 uppercase tracking-wider">Title</Label>
            <Input
              value={lp.title || ""}
              onChange={(e) => setCfg({ ...cfg, live_promo_card: { ...lp, title: e.target.value } })}
              className="bg-white/5 border-white/10 text-white h-9"
              data-testid="hp-live-promo-title"
            />
          </div>
          <div>
            <Label className="text-[11px] text-gray-400 uppercase tracking-wider">Text</Label>
            <Textarea
              value={lp.text || ""}
              onChange={(e) => setCfg({ ...cfg, live_promo_card: { ...lp, text: e.target.value } })}
              className="bg-white/5 border-white/10 text-white min-h-[60px]"
              data-testid="hp-live-promo-text"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] text-gray-400 uppercase tracking-wider">CTA label</Label>
              <Input
                value={lp.cta_label || ""}
                onChange={(e) => setCfg({ ...cfg, live_promo_card: { ...lp, cta_label: e.target.value } })}
                className="bg-white/5 border-white/10 text-white h-9"
                data-testid="hp-live-promo-cta-label"
              />
            </div>
            <div>
              <Label className="text-[11px] text-gray-400 uppercase tracking-wider">CTA link</Label>
              <Input
                value={lp.cta_link || ""}
                onChange={(e) => setCfg({ ...cfg, live_promo_card: { ...lp, cta_link: e.target.value } })}
                className="bg-white/5 border-white/10 text-white h-9"
                data-testid="hp-live-promo-cta-link"
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-gray-400 uppercase tracking-wider">Image URL (optional)</Label>
            <Input
              value={lp.image_url || ""}
              onChange={(e) => setCfg({ ...cfg, live_promo_card: { ...lp, image_url: e.target.value } })}
              className="bg-white/5 border-white/10 text-white h-9"
              placeholder="https://…"
              data-testid="hp-live-promo-image"
            />
          </div>
        </div>
        <div className="mt-4">
          <Button
            className="bg-purple-600 hover:bg-purple-500 text-white"
            disabled={saving === "lp"}
            onClick={() =>
              patchSection(
                "lp",
                "/admin/homepage/live-promo-card",
                {
                  enabled: !!lp.enabled,
                  title: lp.title || "",
                  text: lp.text || "",
                  cta_label: lp.cta_label || "",
                  cta_link: lp.cta_link || "",
                  image_url: lp.image_url || null,
                },
                "Live Promo Card saved",
              )
            }
            data-testid="hp-live-promo-save"
          >
            {saving === "lp" ? "Saving…" : "Save Live Promo"}
          </Button>
        </div>
      </Card>

      {/* 3 · Announcement Presentation */}
      <Card title="📣 Announcement Presentation (defaults)" testId="hp-announcement-card">
        <p className="text-xs text-gray-400 mb-3">
          Applies to all hero slides. Per-slide overrides can be set on the Hero Slides page.
        </p>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label className="text-[11px] text-gray-400 uppercase tracking-wider">Default label size</Label>
            <select
              value={ap.default_label_size || "md"}
              onChange={(e) => setCfg({ ...cfg, announcement_presentation: { ...ap, default_label_size: e.target.value } })}
              className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
              data-testid="hp-ap-label-size"
            >
              {SIZES.map((s) => (
                <option key={s.value} value={s.value} className="bg-[#13131a]">{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[11px] text-gray-400 uppercase tracking-wider">Default title size</Label>
            <select
              value={ap.default_title_size || "md"}
              onChange={(e) => setCfg({ ...cfg, announcement_presentation: { ...ap, default_title_size: e.target.value } })}
              className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
              data-testid="hp-ap-title-size"
            >
              {SIZES.map((s) => (
                <option key={s.value} value={s.value} className="bg-[#13131a]">{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[11px] text-gray-400 uppercase tracking-wider">Default display mode</Label>
            <select
              value={ap.default_display_mode || "full"}
              onChange={(e) => setCfg({ ...cfg, announcement_presentation: { ...ap, default_display_mode: e.target.value } })}
              className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
              data-testid="hp-ap-display-mode"
            >
              {MODES.map((m) => (
                <option key={m.value} value={m.value} className="bg-[#13131a]">{m.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <Button
            className="bg-purple-600 hover:bg-purple-500 text-white"
            disabled={saving === "ap"}
            onClick={() =>
              patchSection(
                "ap",
                "/admin/homepage/announcement-presentation",
                {
                  default_label_size: ap.default_label_size || "md",
                  default_title_size: ap.default_title_size || "md",
                  default_display_mode: ap.default_display_mode || "full",
                },
                "Announcement presentation saved",
              )
            }
            data-testid="hp-ap-save"
          >
            {saving === "ap" ? "Saving…" : "Save presentation"}
          </Button>
        </div>
      </Card>

      {/* 4 · Boost Pulse */}
      <Card title="✨ Boost Pulse controls" testId="hp-boost-pulse-card">
        <div className="flex items-center gap-3 mb-3">
          <input
            id="bp-enabled"
            type="checkbox"
            checked={!!bp.enabled_globally}
            onChange={(e) =>
              setCfg({ ...cfg, boost_pulse: { ...bp, enabled_globally: e.target.checked } })
            }
            className="w-4 h-4"
            data-testid="hp-boost-pulse-enabled"
          />
          <label htmlFor="bp-enabled" className="text-sm text-white">
            Show Boost Pulse widget for everyone
          </label>
        </div>
        <div className="mt-1 mb-4">
          <Button
            className="bg-purple-600 hover:bg-purple-500 text-white"
            disabled={saving === "bp"}
            onClick={() =>
              patchSection(
                "bp",
                "/admin/homepage/boost-pulse",
                { enabled_globally: !!bp.enabled_globally },
                "Boost Pulse settings saved",
              )
            }
            data-testid="hp-boost-pulse-save"
          >
            {saving === "bp" ? "Saving…" : "Save global toggle"}
          </Button>
        </div>

        <div className="border-t border-white/10 pt-3">
          <p className="text-xs text-gray-400 mb-2">Per-user override — disable widget for a specific user by user_id.</p>
          <div className="flex gap-2">
            <Input
              value={disableUid}
              onChange={(e) => setDisableUid(e.target.value)}
              placeholder="user_id (e.g. user_abc123)"
              className="bg-white/5 border-white/10 text-white h-9 flex-1"
              data-testid="hp-boost-pulse-user-input"
            />
            <Button
              variant="outline"
              onClick={() => {
                if (!disableUid.trim()) return;
                toggleUser(disableUid.trim(), true);
                setDisableUid("");
              }}
              className="border-rose-500/40 text-rose-200 hover:bg-rose-500/10"
              data-testid="hp-boost-pulse-disable-user"
            >
              Disable
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (!disableUid.trim()) return;
                toggleUser(disableUid.trim(), false);
                setDisableUid("");
              }}
              className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
              data-testid="hp-boost-pulse-enable-user"
            >
              Re-enable
            </Button>
          </div>
        </div>

        <div className="border-t border-white/10 mt-4 pt-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-gray-400">
              Read-only audit — never mutates.
            </p>
            <Button
              variant="outline"
              onClick={loadAudit}
              disabled={auditLoading}
              className="text-white border-gray-700 h-8"
              data-testid="hp-boost-pulse-audit-refresh"
            >
              {auditLoading ? "Loading…" : audit ? "🔄 Refresh audit" : "▶ Load audit"}
            </Button>
          </div>
          {audit && (
            <div className="mt-3">
              <p className="text-[11px] text-gray-500 mb-2">
                Formulas — reach: {audit.formulas?.reach} · impressions: {audit.formulas?.impressions} · display_plays: {audit.formulas?.display_plays}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="hp-boost-pulse-audit-table">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-white/10">
                      <th className="py-1 pr-2">User</th>
                      <th className="py-1 pr-2">Real plays</th>
                      <th className="py-1 pr-2">Promo</th>
                      <th className="py-1 pr-2">Display</th>
                      <th className="py-1 pr-2">Reach</th>
                      <th className="py-1 pr-2">Impressions</th>
                      <th className="py-1 pr-2">Tracks</th>
                      <th className="py-1 pr-2">Widget</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(audit.rows || []).slice(0, 25).map((r) => (
                      <tr
                        key={r.user_id}
                        className="border-b border-white/5 text-gray-200"
                        data-testid={`hp-boost-pulse-audit-row-${r.user_id}`}
                      >
                        <td className="py-1 pr-2 whitespace-nowrap">{r.name}</td>
                        <td className="py-1 pr-2">{r.real_plays.toLocaleString()}</td>
                        <td className="py-1 pr-2">{r.promotional_plays.toLocaleString()}</td>
                        <td className="py-1 pr-2 text-amber-200">{r.display_plays.toLocaleString()}</td>
                        <td className="py-1 pr-2">{r.reach.toLocaleString()}</td>
                        <td className="py-1 pr-2">{r.impressions.toLocaleString()}</td>
                        <td className="py-1 pr-2">{r.tracks_with_activity}</td>
                        <td className="py-1 pr-2">
                          {r.widget_disabled ? (
                            <button
                              onClick={() => toggleUser(r.user_id, false)}
                              className="text-emerald-300 underline"
                              data-testid={`hp-bp-reenable-${r.user_id}`}
                            >
                              Re-enable
                            </button>
                          ) : (
                            <button
                              onClick={() => toggleUser(r.user_id, true)}
                              className="text-rose-300 underline"
                              data-testid={`hp-bp-disable-${r.user_id}`}
                            >
                              Disable
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-gray-500 mt-2">{audit.note} · {audit.count} rows</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default HomepageControlsSection;
